import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SendIcon from '@mui/icons-material/Send';
import UndoIcon from '@mui/icons-material/Undo';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { transactionsActions } from 'app-components/flux/transactions';
import { getAvailableCredit } from 'app-components/flux/transactions/invoicesSelectors';
import { InvoicePaymentUpsertAction } from 'components/InvoicePayment/actions';
import { InvoiceSendUpsertAction } from 'components/InvoiceSendModal/actions';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { DateTime } from 'luxon';
import React from 'react';
import type { AppDispatch } from 'store';
import { formatAsCurrency } from 'utils/formatters';
import type { TxnOptionProps } from './types';

type ApplyPaymentSubmenuProps = {
  recordPayment: boolean;
  availableCredit: number;
  txnId: string | null | undefined;
  dispatch: AppDispatch;
} & Pick<TxnOptionProps, 'closeMenu' | 'context'>;

const buildApplyPaymentSubmenu = ({
  recordPayment,
  availableCredit,
  closeMenu,
  context,
  txnId,
  dispatch,
}: ApplyPaymentSubmenuProps) => {
  return [
    {
      label: 'Link to Transaction',
      value: 'link-to-transaction',
      subtext: 'Choose from one of your downloaded transactions ',
      onSelect: () => {
        closeMenu();
        dispatch(
          InvoicePaymentUpsertAction({
            action: recordPayment ? 'RECORD_PAYMENT' : 'RECORD_REFUND',
            context,
            initIdType: 'INVOICE',
            initId: txnId,
            initScreen: 'SELECTION',
          }),
        );
      },
    },
    {
      label: 'Create Transaction',
      value: 'create-manual-txn',
      subtext: recordPayment ? 'Create and apply a payment' : 'Create and apply a refund',
      onSelect: () => {
        closeMenu();
        dispatch(
          InvoicePaymentUpsertAction({
            action: recordPayment ? 'RECORD_PAYMENT' : 'RECORD_REFUND',
            context,
            initIdType: 'INVOICE',
            initId: txnId,
            initScreen: 'MAIN',
          }),
        );
      },
    },
    ...(recordPayment
      ? [
          {
            label: 'Use Existing Credit',
            value: 'apply-existing-credit',
            subtext: `Client credit balance is ${formatAsCurrency(availableCredit)}`,
            disabled: !availableCredit || availableCredit < 0.005,
            onSelect: () => {
              closeMenu();
              dispatch(
                InvoicePaymentUpsertAction({
                  action: 'ASSIGN_CREDITS',
                  context,
                  initIdType: 'INVOICE',
                  initId: txnId,
                  initScreen: 'MAIN',
                }),
              );
            },
          },
        ]
      : []),
  ];
};

export const useInvoiceOptionsRaw = (
  props: TxnOptionProps & {
    /** Used if we only want to show payment related options. */
    paymentOnly?: boolean;
  },
) => {
  const { closeMenu, context, txnActionProps, paymentOnly } = props;
  const { attributes, txnToEdit: txn } = txnActionProps;
  const dispatch = useAppDispatch();

  const enableRefunds = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'businessRefunds'));

  const availableCredit = useAppSelector((state) =>
    getAvailableCredit(state, {
      businessId: txn.businessId,
      customerId: txn.customerId,
      projectId: txn.projectId || 'no-project',
    }),
  );

  const options: any[] = [];
  if (
    txn.type !== 'INVOICE' ||
    (txn.subtype && txn.subtype !== 'INVOICE' && txn.subtype !== 'INVOICE_CREDIT_MEMO') ||
    attributes.txnToEditWasFlipped
  ) {
    return options;
  }

  const recordPayment = txn.subtype !== 'INVOICE_CREDIT_MEMO';

  if (!attributes.txnToEditWasFlipped && attributes.hasAllocations) {
    options.push({
      id: 'txn-menu-edit-invoice-allocations',
      key: 'txn-menu-edit-invoice-allocations',
      startIcon: <PaymentsOutlinedIcon />,
      label: 'Edit Assignments',
      onSelect: () => {
        closeMenu();
        dispatch(
          InvoicePaymentUpsertAction({
            action: 'EDIT_ALLOCATIONS',
            context,
            initId: txn.id,
            initScreen: 'MAIN',
            initCustomerId: txn.customerId,
          }),
        );
      },
    });
  }

  if (!attributes.isFullyAllocated) {
    if (!attributes.hasAllocations && txn.subtype === 'INVOICE_CREDIT_MEMO') {
      options.push({
        id: 'txn-menu-apply-to-invoice',
        key: 'txn-menu-apply-to-invoice',
        startIcon: <PaymentsOutlinedIcon />,
        label: 'Apply to Invoice',
        onSelect: () => {
          closeMenu();
          dispatch(
            InvoicePaymentUpsertAction({
              action: 'EDIT_ALLOCATIONS',
              context,
              initId: txn.id,
              initScreen: 'MAIN',
            }),
          );
        },
      });
    }
    if (recordPayment || enableRefunds) {
      const subMenu = buildApplyPaymentSubmenu({
        dispatch,
        recordPayment,
        context,
        txnId: txn.id,
        closeMenu,
        availableCredit,
      }) as any[];

      const recordOptions = {
        id: 'txn-menu-allocate',
        key: 'txn-menu-allocate',
        startIcon: recordPayment ? <PaymentsOutlinedIcon /> : <UndoIcon />,
        label: recordPayment ? 'Apply Payment' : 'Refund',
        subMenu,
      };

      options.push(recordOptions);
    }
  }

  // add if more than one edit/record menu item
  if (options.length > 1 && !paymentOnly) {
    options[options.length - 1].divider = true;
  }

  if (!txn.sentOn && !paymentOnly) {
    options.push({
      id: 'mark-as-sent',
      label: 'Send',
      startIcon: <SendIcon />,
      subMenu: [
        {
          label: 'Mark as Sent',
          onSelect: () => {
            closeMenu();
            dispatch(transactionsActions.updateTransaction(txn.set('sentOn', DateTime.now().toISODate())), {
              context,
              undo: { userMessage: 'Transaction updated.' },
            });
          },
        },
        {
          label: 'Send via Email',
          onSelect: () => {
            closeMenu();
            dispatch(
              InvoiceSendUpsertAction({
                context,
                formComponentProps: { invoice: txn },
              }),
            );
          },
        },
      ],
    });
  }

  // add divider on last option
  if (options.length > 0 && !paymentOnly) {
    options[options.length - 1].divider = true;
  }

  return options;
};
